import React from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronLeft, ChevronRight, Expand, MessageCircle, X } from 'lucide-react';
import {
  coLearningExpandedBackdropProps,
  coLearningGalleryPickerBackdropProps,
  coLearningInkBody,
  coLearningInkMuted,
  coLearningInkTitle,
} from '../ui/coLearningModalTheme';

function getFeedbackList(activity) {
  let feedback = activity?.metadata?.artmosphereFeedback;
  if (!feedback && Array.isArray(activity?.metadata?.transformations)) {
    const last = activity.metadata.transformations[activity.metadata.transformations.length - 1];
    feedback = last?.artmosphereFeedback;
  }
  if (!Array.isArray(feedback)) return [];
  return feedback.filter((item) => {
    const title = (item.title || item.label || '').toLowerCase();
    const text = item.text || item.appreciation || '';
    if (title === 'intro' && text.length < 80) return false;
    return true;
  });
}

function PremiumPanel({ label, children, accent = 'amber' }) {
  const accentBorder =
    accent === 'rose'
      ? 'border-[rgba(251,191,36,0.35)] bg-gradient-to-br from-[#FFF9EF] to-[#FFF3DC]'
      : 'border-[rgba(245,203,92,0.45)] bg-gradient-to-br from-[#FFFDF9] to-[#FFF7ED]';

  return (
    <div className={`rounded-2xl border px-4 py-3.5 shadow-[0_6px_18px_rgba(120,72,20,0.06)] ${accentBorder}`}>
      <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#9A7A55]">{label}</p>
      <div className={`text-sm leading-relaxed ${coLearningInkBody}`}>{children}</div>
    </div>
  );
}

/**
 * Premium ARTmosphere modal — Re-imagined artwork details.
 */
export default function ReimaginedArtworkDetailsModal({
  open,
  onClose,
  originalArtworkUrl,
  transformedImageUrl,
  activity,
  expandedFeedback = {},
  onToggleFeedback,
  onExpandImage,
  showNavigation = false,
  currentIndex = 0,
  totalCount = 1,
  onPrevious,
  onNext,
}) {
  if (!open) return null;

  const prompt = activity?.metadata?.prompt || 'No prompt available';
  const reflection = activity?.metadata?.userFeedback || 'No reflection provided';
  const feedbackItems = getFeedbackList(activity);

  return createPortal(
    <>
      <div
        {...coLearningGalleryPickerBackdropProps}
        className={`${coLearningGalleryPickerBackdropProps.className} z-[200050]`}
        onClick={onClose}
        role="presentation"
      >
        <div
          className="relative flex max-h-[min(94dvh,920px)] w-full max-w-4xl flex-col overflow-hidden rounded-[28px] border border-[#F6D57B]/80 bg-[rgba(255,255,255,0.94)] shadow-[0_28px_80px_rgba(120,72,20,0.18)] backdrop-blur-xl sm:rounded-[32px]"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="reimagined-artwork-modal-title"
        >
          <div
            className="h-1 w-full shrink-0 bg-gradient-to-r from-[#F59E0B] via-[#FBBF24] to-[#F97316]"
            aria-hidden
          />

          <header className="relative shrink-0 border-b border-[#F5D99B]/50 bg-[#FFFDF9]/95 px-4 py-3.5 backdrop-blur-md sm:px-6 sm:py-4">
            <div
              className="pointer-events-none absolute inset-x-0 top-0 h-14 bg-[radial-gradient(ellipse_at_50%_0%,rgba(255,214,140,0.35),transparent_70%)]"
              aria-hidden
            />
            <div className="relative flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#9A7A55]">
                  Ways of Seeing
                </p>
                <h2
                  id="reimagined-artwork-modal-title"
                  className="font-serif text-lg font-semibold leading-tight text-[#2F251B] sm:text-xl md:text-2xl"
                >
                  Re-imagined Artwork
                </h2>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#E8C97A] bg-[#FFF9EF] text-[#5C4428] shadow-sm transition hover:border-[#F5CB5C] hover:bg-white hover:shadow-md"
                aria-label="Close"
              >
                <X className="h-4 w-4" strokeWidth={2.25} />
              </button>
            </div>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
              <div className="space-y-4">
                <div>
                  <h3 className={`mb-2 text-sm font-semibold ${coLearningInkTitle}`}>Original Artwork</h3>
                  <div className="overflow-hidden rounded-2xl border border-[#F5D99B]/60 bg-[#FFF9EF] shadow-[0_8px_24px_rgba(120,72,20,0.08)]">
                    <img
                      src={originalArtworkUrl}
                      alt="Original artwork"
                      className="h-44 w-full object-cover sm:h-56 md:h-64"
                    />
                  </div>
                </div>

                <div>
                  <h3 className={`mb-2 text-sm font-semibold ${coLearningInkTitle}`}>Transformed Artwork</h3>
                  <div className="relative overflow-hidden rounded-2xl border border-[#F5D99B]/60 bg-[#FFF9EF] shadow-[0_8px_24px_rgba(120,72,20,0.08)]">
                    <img
                      src={transformedImageUrl}
                      alt="Transformed artwork"
                      className="h-44 w-full object-cover sm:h-56 md:h-64"
                    />
                    {onExpandImage ? (
                      <button
                        type="button"
                        onClick={onExpandImage}
                        className="absolute right-2 top-2 inline-flex items-center justify-center rounded-full border border-white/70 bg-white/90 p-2 text-[#5C4428] shadow-md backdrop-blur-sm transition hover:-translate-y-0.5 hover:bg-white sm:right-3 sm:top-3"
                        title="Expand artwork"
                      >
                        <Expand className="h-4 w-4" strokeWidth={2} />
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <PremiumPanel label="How You Reimagined Your Artwork">{prompt}</PremiumPanel>
                <PremiumPanel label="Your Reflection">{reflection}</PremiumPanel>

                <div>
                  <h3 className={`mb-3 flex items-center gap-2 text-sm font-semibold ${coLearningInkTitle}`}>
                    <MessageCircle className="h-4 w-4 text-[#F59E0B]" strokeWidth={2} aria-hidden />
                    Artmosphere&apos;s Insights
                  </h3>

                  {feedbackItems.length > 0 ? (
                    <div className="space-y-2.5">
                      {feedbackItems.map((feedback, index) => {
                        const title = feedback.title || feedback.label || '';
                        const text = feedback.text || feedback.appreciation || '';
                        const isSuggestionOnly = !!feedback.suggestion && !text;
                        const displayTitle = isSuggestionOnly
                          ? "Mentor's Suggestion"
                          : title || 'Artmosphere Insight';
                        const isOpen = !!expandedFeedback[index];

                        return (
                          <div
                            key={index}
                            className="overflow-hidden rounded-2xl border border-[#F5D99B]/70 bg-gradient-to-br from-[#FFFDF9] to-[#FFF5E8] shadow-[0_6px_18px_rgba(120,72,20,0.06)]"
                          >
                            <button
                              type="button"
                              onClick={() => onToggleFeedback?.(index)}
                              className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left transition hover:bg-[#FFF9EF]/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F59E0B]/30"
                            >
                              <div className="min-w-0">
                                <h4 className="text-sm font-semibold text-[#2F251B]">{displayTitle}</h4>
                                <p className={`mt-0.5 text-[11px] ${coLearningInkMuted}`}>
                                  {isOpen ? 'Tap to collapse' : 'Tap to read'}
                                </p>
                              </div>
                              <ChevronDown
                                className={`h-4 w-4 shrink-0 text-[#B45309] transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                                strokeWidth={2.25}
                              />
                            </button>
                            <div
                              className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                                isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                              }`}
                            >
                              <div className="overflow-hidden">
                                <div className="border-t border-[#F5D99B]/50 bg-white/60 px-4 py-3.5">
                                  {text ? (
                                    <p className={`text-sm leading-relaxed ${coLearningInkBody}`}>{text}</p>
                                  ) : null}
                                  {feedback.suggestion ? (
                                    <p
                                      className={`text-sm leading-relaxed ${coLearningInkBody} ${text ? 'mt-2 border-t border-[#F5D99B]/40 pt-2' : ''}`}
                                    >
                                      {feedback.suggestion}
                                    </p>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-dashed border-[#F5D99B] bg-[#FFF9EF]/80 px-4 py-8 text-center">
                      <p className={`text-sm font-medium ${coLearningInkBody}`}>No insights available yet</p>
                      <p className={`mt-1 text-xs ${coLearningInkMuted}`}>
                        Check back later for personalized feedback
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {showNavigation && totalCount > 1 ? (
              <div className="mt-6 flex items-center justify-center gap-3 border-t border-[#F5D99B]/50 pt-5">
                <button
                  type="button"
                  onClick={onPrevious}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#E8C97A] bg-[#FFF9EF] px-4 py-2 text-xs font-semibold text-[#5C4428] shadow-sm transition hover:border-[#F5CB5C] hover:shadow-md sm:text-sm"
                >
                  <ChevronLeft className="h-4 w-4" strokeWidth={2} />
                  Previous
                </button>
                <span className={`text-xs font-semibold sm:text-sm ${coLearningInkMuted}`}>
                  {currentIndex + 1} of {totalCount}
                </span>
                <button
                  type="button"
                  onClick={onNext}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#E8C97A] bg-[#FFF9EF] px-4 py-2 text-xs font-semibold text-[#5C4428] shadow-sm transition hover:border-[#F5CB5C] hover:shadow-md sm:text-sm"
                >
                  Next
                  <ChevronRight className="h-4 w-4" strokeWidth={2} />
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}

/** Stacked above ReimaginedArtworkDetailsModal (z-[200050]) and other premium activity shells */
const REIMAGINED_EXPANDED_Z =
  'fixed inset-0 z-[200060] flex items-center justify-center bg-[rgba(253,238,216,0.78)] px-2 py-3 backdrop-blur-[40px] backdrop-saturate-150 sm:px-3 sm:py-5';

export function ReimaginedArtworkExpandedView({ open, imageUrl, onClose }) {
  if (!open || !imageUrl) return null;

  return createPortal(
    <div
      className={REIMAGINED_EXPANDED_Z}
      style={coLearningExpandedBackdropProps.style}
      onClick={onClose}
      role="presentation"
    >
      <button
        type="button"
        className="absolute right-4 top-4 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#E8C97A] bg-[#FFF9EF] text-[#5C4428] shadow-lg transition hover:bg-white sm:right-6 sm:top-6"
        onClick={onClose}
        aria-label="Close expanded view"
      >
        <X className="h-5 w-5" strokeWidth={2.25} />
      </button>
      <img
        src={imageUrl}
        alt="Expanded transformed artwork"
        className="max-h-[min(88dvh,900px)] max-w-[min(96vw,1100px)] rounded-2xl border border-[#F6D57B]/70 bg-white object-contain shadow-[0_24px_70px_rgba(120,72,20,0.2)]"
        onClick={(e) => e.stopPropagation()}
      />
    </div>,
    document.body
  );
}
